import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  LinearProgress,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Cable, FlashOn, FormatAlignLeft, Upload } from '@mui/icons-material';
import { Firmware, FileStore } from '../types';
import { firmwareApi, fileStoreApi } from '../api';
import { FLASH_PROFILES, productLabel, profileForEnvironment } from '../flash/profiles';
import { ConfigSchema, ConfigValues, generateConfigJson, mergeConfig } from '../schema/schema';
import SchemaForm from './SchemaForm';
import ConfigMigrateBanner from './ConfigMigrateBanner';
import SerialConsole from './SerialConsole';
import UsbFileList from './UsbFileList';
import { useUsbSession } from '../flash/UsbSessionContext';
import { SpiffsEntry } from '../flash/serialSession';
import {
  flashFirmware,
  reopenConsolePort,
  requestSerialPort,
  webSerialSupported,
} from '../flash/esptool';
import { useI18n } from '../i18n';

function apiError(err: any, fallback: string): string {
  return err?.response?.data?.error || err?.message || fallback;
}

const FlashDevice: React.FC = () => {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const usb = useUsbSession();
  const [store, setStore] = useState<FileStore | null>(null);
  const [firmware, setFirmware] = useState<Firmware[]>([]);
  const [environment, setEnvironment] = useState(
    profileForEnvironment(searchParams.get('env') || '')?.environment || FLASH_PROFILES[0].environment,
  );
  const [filename, setFilename] = useState('');
  const [configSource, setConfigSource] = useState<'template' | 'device' | 'none'>('template');
  const [templateId, setTemplateId] = useState('');
  const [deviceId, setDeviceId] = useState(searchParams.get('id') || searchParams.get('device') || '');
  const [settings, setSettings] = useState('');
  const [schema, setSchema] = useState<ConfigSchema | null>(null);
  const [formValues, setFormValues] = useState<ConfigValues>({});
  const [view, setView] = useState<'form' | 'json'>('form');
  const [editorPath, setEditorPath] = useState('/settings.json');
  const [log, setLog] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [percent, setPercent] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [resultId, setResultId] = useState<string | null>(null);

  const profile = profileForEnvironment(environment) || FLASH_PROFILES[0];
  const serialOk = webSerialSupported();
  const schemaForPath = schema && editorPath === schema.path ? schema : null;
  const locked = busy || usb.busy;

  const appendLog = (chunk: string) => {
    setLog((prev) => (prev + chunk).slice(-12000));
  };

  const applyEditor = (path: string, raw: string) => {
    setEditorPath(path);
    setSettings(raw);
    if (schema && path === schema.path) {
      try {
        setFormValues(mergeConfig(schema, raw));
        setView('form');
      } catch {
        setView('json');
      }
    } else {
      setView('json');
    }
  };

  useEffect(() => {
    fileStoreApi.getStore()
      .then((next) => {
        setStore(next);
        if (!templateId && next.templates[0]) {
          setTemplateId(next.templates[0].id);
        }
      })
      .catch((err) => setError(apiError(err, t('flash.templatesFailed'))));
    fileStoreApi.getSchema()
      .then((next) => {
        setSchema(next);
        setFormValues(mergeConfig(next, settings));
      })
      .catch((err) => setError(apiError(err, t('flash.schemaFailed'))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    firmwareApi.getFirmware(environment)
      .then((list) => {
        const latest = list.find((f) => f.isLatest) || list[0];
        setFirmware(list);
        setFilename(latest?.filename || '');
      })
      .catch((err) => setError(apiError(err, t('flash.firmwareFailed'))));
  }, [environment, t]);

  useEffect(() => {
    const load = async () => {
      try {
        if (configSource === 'none') {
          return;
        }
        if (configSource === 'template' && templateId) {
          const file = await fileStoreApi.getTemplateFile(templateId, '/settings.json');
          applyEditor('/settings.json', file?.content || '');
          return;
        }
        if (configSource === 'device' && deviceId) {
          const file = await firmwareApi.getStoredSettings(deviceId);
          applyEditor('/settings.json', file || '');
        }
      } catch (err: any) {
        setError(apiError(err, t('flash.settingsFailed')));
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configSource, templateId, deviceId, schema]);

  const suggestedTemplate = useMemo(() => {
    if (!store) return '';
    const model = profile.chipFamily.toLowerCase();
    const match = store.templates.find((t) => t.model && model.includes(t.model.toLowerCase()));
    return match?.id || store.templates[0]?.id || '';
  }, [store, profile]);

  useEffect(() => {
    if (suggestedTemplate && configSource === 'template') {
      setTemplateId(suggestedTemplate);
    }
  }, [suggestedTemplate, configSource]);

  const handlePretty = () => {
    try {
      setSettings(JSON.stringify(JSON.parse(settings), null, 2));
      setError(null);
    } catch {
      setError(t('flash.invalidJson'));
    }
  };

  const handleOpenUsbFile = async (file: SpiffsEntry) => {
    const body = await usb.readFile(file.path);
    applyEditor(file.path, body);
    setStatus(t('flash.loadedFromBoard', { path: file.path }));
  };

  const handleWriteUsb = async () => {
    if (!settings) {
      setError(t('flash.editorEmpty'));
      return;
    }
    if (editorPath.endsWith('.json')) {
      try {
        JSON.parse(settings);
      } catch {
        setError(t('flash.editorInvalidJson'));
        return;
      }
    }
    try {
      await usb.connect();
      if (editorPath === '/settings.json') {
        await usb.provisionSettings(settings);
      } else {
        await usb.writeFile(editorPath, settings);
        await usb.refreshFiles();
      }
      setStatus(t('flash.wrotePath', { path: editorPath }));
      if (editorPath === '/settings.json') {
        setStatus(t('flash.wroteSettings'));
      }
    } catch (err: any) {
      setError(apiError(err, t('flash.usbWriteFailed')));
    }
  };

  const handleFlash = async () => {
    if (configSource !== 'none') {
      try {
        JSON.parse(settings);
      } catch {
        setError(t('flash.invalidJson'));
        return;
      }
    }
    setBusy(true);
    setError(null);
    setResultId(null);
    setPercent(0);
    setLog('');
    setStatus(t('flash.flashing'));
    try {
      const existingPort = await usb.takePort();
      if (!existingPort) {
        setStatus(t('flash.choosePort'));
      }
      const port = existingPort || await requestSerialPort();
      setStatus(t('flash.detecting'));
      const flashed = await flashFirmware({
        port,
        profile,
        getImage: async () => {
          const chosen = filename
            || (await firmwareApi.getLatestFirmware(environment))?.filename;
          if (!chosen) {
            throw new Error(t('flash.noFirmwareThrow'));
          }
          setStatus(t('flash.downloading', { name: chosen }));
          setFilename(chosen);
          return firmwareApi.downloadFirmwareBytes(chosen);
        },
        onProgress: (progress) => {
          if (progress.percent >= 0) {
            setPercent(progress.percent);
          }
          if (progress.message) {
            appendLog(`${progress.message}\n`);
          }
        },
      });
      appendLog(`\n${t('flash.chip', { chip: flashed.chip })}\n`);
      if (flashed.profile.environment !== environment) {
        appendLog(`${t('flash.usingProfile', { label: flashed.profile.label })}\n`);
      }
      const mac = flashed.mac;
      if (mac) {
        appendLog(`${t('flash.mac', { mac })}\n`);
      }

      let savedId: string | null = null;
      if (mac) {
        setStatus(t('flash.savingServer'));
        const provisioned = await firmwareApi.provision({
          mac,
          model: flashed.profile.chipFamily,
          settings: configSource === 'none' ? undefined : settings,
        });
        savedId = provisioned.deviceId;
        setResultId(savedId);
      }

      let serialWrote = false;
      setStatus(t('flash.openingUsb'));
      setPercent(100);
      const consolePort = await reopenConsolePort(port, appendLog);
      await usb.attach(consolePort);
      try {
        if (configSource !== 'none' && settings) {
          setEditorPath('/settings.json');
          await usb.provisionSettings(settings);
          serialWrote = true;
        }
      } catch (serialErr: any) {
        appendLog(`\n${t('flash.provisionLog', { error: serialErr?.message || serialErr })}\n`);
        setError(
          t('flash.provisionFailedHint', { error: serialErr?.message || t('flash.provisionFailed') })
        );
      }

      setStatus(
        savedId
          ? t('flash.doneWithId', { id: savedId, written: serialWrote ? t('flash.writtenSettings') : '' })
          : t('flash.done')
      );
      setPercent(100);
    } catch (err: any) {
      setError(apiError(err, t('flash.flashFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 4, px: { xs: 2, md: 3 } }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('flash.intro')}
      </Typography>

      {!serialOk && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('flash.webSerialMissing')}
        </Alert>
      )}
      {(error || usb.error) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setError(null); usb.clearError(); }}>
          {error || usb.error}
        </Alert>
      )}
      {status && !error && !usb.error && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              overflow: 'hidden',
              minWidth: 0,
              py: 0.25,
            },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>{status}</Box>
          {locked && <CircularProgress size={16} sx={{ flexShrink: 0 }} />}
        </Alert>
      )}
      {resultId && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('flash.storedAs', { id: resultId })}
        </Alert>
      )}

      <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <TextField
            select
            fullWidth
            margin="dense"
            label={t('flash.board')}
            value={environment}
            onChange={(e) => setEnvironment(e.target.value)}
            disabled={locked}
          >
            {FLASH_PROFILES.map((item) => (
              <MenuItem key={item.environment} value={item.environment}>
                {productLabel(item.environment, t)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            margin="dense"
            label={t('flash.firmware')}
            helperText={t('flash.firmwareHint')}
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            disabled={locked || firmware.length === 0}
          >
            {firmware.map((item) => (
              <MenuItem key={item.filename} value={item.filename}>
                {item.isLatest ? t('flash.latestPrefix') : ''}
                {item.environment ? `${productLabel(item.environment, t)} — ` : ''}
                {item.versionString || item.filename}
              </MenuItem>
            ))}
          </TextField>
          {firmware.length === 0 && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {t('flash.noFirmware')}
            </Alert>
          )}

          <TextField
            select
            fullWidth
            margin="dense"
            label={t('flash.configSource')}
            value={configSource}
            onChange={(e) => setConfigSource(e.target.value as 'template' | 'device' | 'none')}
            disabled={locked}
          >
            <MenuItem value="template">{t('flash.sourceTemplate')}</MenuItem>
            <MenuItem value="device">{t('flash.sourceDevice')}</MenuItem>
            <MenuItem value="none">{t('flash.sourceNone')}</MenuItem>
          </TextField>
          {configSource === 'template' && (
            <TextField
              select
              fullWidth
              margin="dense"
              label={t('common.template')}
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
              disabled={locked}
            >
              {(store?.templates || []).map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}{template.model ? ` (${template.model})` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
          {configSource === 'device' && (
            <TextField
              select
              fullWidth
              margin="dense"
              label={t('common.device')}
              value={deviceId}
              onChange={(e) => setDeviceId(e.target.value)}
              disabled={locked}
            >
              {(store?.devices || []).map((device) => (
                <MenuItem key={device.id} value={device.id}>
                  {device.hostname || device.id}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box display="flex" gap={1} mt={2} mb={1}>
            <Chip size="small" label={t('flash.appOffset', { offset: profile.appOffset.toString(16) })} />
            <Chip size="small" label={profile.flashSize} />
            <Chip size="small" icon={<Cable />} label={t('common.webSerial')} />
          </Box>

          <Button
            variant="contained"
            startIcon={<FlashOn />}
            onClick={handleFlash}
            disabled={locked || !serialOk}
            fullWidth
            sx={{ mt: 2 }}
          >
            {t('flash.connectAndFlash')}
          </Button>
          {percent !== null && (
            <LinearProgress variant="determinate" value={percent} sx={{ mt: 2 }} />
          )}

          <Box mt={2}>
            <UsbFileList selectedPath={editorPath} onOpen={handleOpenUsbFile} disabled={locked} />
          </Box>
        </Paper>

        <Paper sx={{ p: 2, flex: 1.4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} gap={1} flexWrap="wrap">
            <Typography variant="subtitle1">{editorPath}</Typography>
            <Box>
              {schemaForPath && (
                <>
                  <Button size="small" onClick={() => setView('form')} variant={view === 'form' ? 'contained' : 'text'}>
                    {t('common.form')}
                  </Button>
                  <Button size="small" onClick={() => setView('json')} variant={view === 'json' ? 'contained' : 'text'}>
                    {t('common.json')}
                  </Button>
                </>
              )}
              <Button
                size="small"
                startIcon={<FormatAlignLeft />}
                onClick={handlePretty}
                disabled={locked || !settings || view === 'form'}
              >
                {t('common.format')}
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<Upload />}
                onClick={handleWriteUsb}
                disabled={locked || !usb.connected || !settings}
              >
                {t('flash.writeToUsb')}
              </Button>
            </Box>
          </Box>
          {schemaForPath && (
            <ConfigMigrateBanner
              schema={schemaForPath}
              raw={settings}
              disabled={locked}
              onMigrated={(next) => {
                setSettings(next);
                setFormValues(mergeConfig(schemaForPath, next));
                setView('json');
                setStatus(t('flash.migrated', { version: schemaForPath.configVersion }));
              }}
            />
          )}
          {schemaForPath && view === 'form' ? (
            <SchemaForm
              schema={schemaForPath}
              values={formValues}
              disabled={locked}
              onChange={(next) => {
                setFormValues(next);
                setSettings(generateConfigJson(schemaForPath, next, settings));
              }}
            />
          ) : (
            <TextField
              value={settings}
              onChange={(e) => setSettings(e.target.value)}
              disabled={locked}
              multiline
              minRows={18}
              maxRows={32}
              fullWidth
              InputProps={{
                sx: { fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 13 },
              }}
            />
          )}
        </Paper>
      </Box>

      <Box mt={2}>
        <SerialConsole locked={busy} />
      </Box>

      {log && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('flash.flashLog')}
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
              fontSize: 12,
              maxHeight: 240,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {log}
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default FlashDevice;
